import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Clock } from '../../../icons';
import { useFormField } from '../FormField';
import { parseHm, toHm } from './calendar-core';
import './DatePicker.css';

/**
 * TimePicker — 시각만 (디자인 정본 C4).
 * 쓰이는 곳: 근무제 시작·종료 시각 · 출퇴근 수정 · 교대 근무표 · 예약 자원 운영시간.
 *
 * ★★★ **24시간제 고정.** 근무 시각에서 「9:00」은 오전인지 오후인지가 **일과 관련된 사실**이라
 *    오전/오후 표기는 잘못 읽히는 순간 근태 기록이 틀린다.
 *
 * ★★ **분은 촘촘하게 고르지 않는다** — 기본 10분 간격이다(`minuteStep`).
 *    1분 단위로 60줄을 굴리게 하면 고르는 데 시간이 더 든다. 정확한 값이 필요하면 직접 적는다.
 *
 * ★ 값은 `HH:mm` 문자열이다 — `Date` 로 주고받으면 «어느 날의» 9시인지가 따라붙는다.
 */
export interface TimePickerProps {
  /** `HH:mm` 또는 빈 값. */
  value: string | null;
  onChange: (next: string | null) => void;
  /** 분 눈금. 기본 10. */
  minuteStep?: number;
  min?: string | null;
  max?: string | null;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function TimePicker({
  value,
  onChange,
  minuteStep = 10,
  min,
  max,
  placeholder = '시각',
  disabled,
  invalid,
  ariaLabel,
  className,
}: TimePickerProps) {
  const field = useFormField();
  const uid = useId();
  const popId = `${uid}-pop`;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLDivElement>(null);
  const minRef = useRef<HTMLDivElement>(null);

  const current = value ? parseHm(value) : null;
  const hour = current == null ? null : Math.floor(current / 60);
  const minute = current == null ? null : current % 60;

  const minM = min ? parseHm(min) : null;
  const maxM = max ? parseHm(max) : null;
  const allowed = (m: number) => (minM == null || m >= minM) && (maxM == null || m <= maxM);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc, true);
    };
  }, [open]);

  /** 열자마자 고른 값이 보이게 굴려 둔다 — 09:00 을 고르려고 아침부터 스크롤하지 않게. */
  useLayoutEffect(() => {
    if (!open) return;
    for (const [ref, sel] of [[hourRef, '.is-on'], [minRef, '.is-on']] as const) {
      const el = ref.current?.querySelector<HTMLElement>(sel);
      if (el && ref.current) ref.current.scrollTop = el.offsetTop - ref.current.clientHeight / 2 + el.clientHeight / 2;
    }
  }, [open]);

  const isDisabled = disabled ?? field?.disabled ?? false;
  const isInvalid = invalid ?? field?.invalid ?? false;

  const setPart = (h: number | null, m: number | null) => {
    const nh = h ?? hour ?? 0;
    const nm = m ?? minute ?? 0;
    onChange(toHm(nh * 60 + nm));
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);

  return (
    <div
      ref={rootRef}
      className={['wc-datepicker', 'wc-timepicker', isInvalid ? 'wc-datepicker--invalid' : '', className ?? '']
        .filter(Boolean).join(' ')}
    >
      <button
        type="button"
        id={field?.controlId}
        className="wc-datepicker__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-describedby={field?.describedBy}
        aria-invalid={isInvalid || undefined}
        aria-required={field?.required || undefined}
        aria-label={field?.controlId ? undefined : (ariaLabel ?? placeholder)}
        disabled={isDisabled}
        onClick={() => setOpen((v) => !v)}
      >
        <Clock className="wc-datepicker__icon" aria-hidden />
        <span className={value ? 'wc-datepicker__value' : 'wc-datepicker__placeholder'}>{value ?? placeholder}</span>
      </button>

      {open && (
        <div className="wc-datepicker__pop wc-timepicker__pop" id={popId} role="dialog" aria-label="시각 고르기">
          <div className="wc-timepicker__col" ref={hourRef} role="listbox" aria-label="시">
            <span className="wc-timepicker__colhead">시</span>
            {hours.map((h) => {
              const disabledOpt = !allowed(h * 60 + (minute ?? 0));
              return (
                <button key={h} type="button" role="option" aria-selected={hour === h}
                  className={`wc-timepicker__opt${hour === h ? ' is-on' : ''}`}
                  disabled={disabledOpt}
                  onClick={() => setPart(h, null)}>
                  {String(h).padStart(2, '0')}
                </button>
              );
            })}
          </div>
          <div className="wc-timepicker__col" ref={minRef} role="listbox" aria-label="분">
            <span className="wc-timepicker__colhead">분</span>
            {minutes.map((m) => {
              const disabledOpt = !allowed((hour ?? 0) * 60 + m);
              return (
                <button key={m} type="button" role="option" aria-selected={minute === m}
                  className={`wc-timepicker__opt${minute === m ? ' is-on' : ''}`}
                  disabled={disabledOpt}
                  onClick={() => { setPart(null, m); setOpen(false); }}>
                  {String(m).padStart(2, '0')}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
